import { Form, Input, Select, Collapse, Alert } from 'antd';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom-v5-compat';
import { SIMULATION_ID_SEARCH_PARAM } from '../../../pages/Simulation/SimulationLandingPage';
import { useStoreState, useStoreActions } from '../../../store';
import { BaseFeatureToggles } from '../../../store/navigation/BaseFeatureToggles';
import type { SimulationType } from '../../../store/simulation/types';
import { SimulationStatus } from '../../../store/simulation/types';
import { DEFAULT_SIMULATION_SERVICE_VERSION } from '../../../store/simulation/types';
import type { SimulationServiceVersion } from '../../../store/simulation/types';
import { VersionPicker } from './VersionPicker';
import { CostAdjustLimitsForm } from '../PartnerConfigurationOverrideForms/CostAdjustLimitsForm';
import { CostingOptionsForm } from '../PartnerConfigurationOverrideForms/CostingOptionsForm';
import { ShipmentOptionsForm } from '../PartnerConfigurationOverrideForms/ShipmentOptionsForm';
import { shapeShipOptions, flattenBaseDetailsToFormData, formatCarrierListsForStorage } from '../utils';
import { SimulationWorkflowCard } from './SimulationWorkflowCard';
import { useCreateSimulation, useUpdateSimulation } from '~/hooks/simulation/useSimulationSetupMutations';
import { usePartnerCarrierSummaries } from '~/hooks/queries/usePartnerCarrierSummaries';
import { useSession } from '~/hooks/useSession';
import { DYNAMIC_LIMIT_EVALUATION_PERIOD } from '../../limit/LimitUtils';
import { DEFAULT_LIMIT_BEHAVIOR, doubleToPercent, percentToClampedFixedDouble } from '../../../store/limit/utils';
import type { PartnerCostAdjustConfigCreate, PartnerCostAdjustConfigPut } from '@shipium-corp/shipium-ts-types';

const SimulationPrioties = [
  {
    title: 'Low',
    value: 'low',
  },
  {
    title: 'Standard',
    value: 'standard',
  },
  {
    title: 'High',
    value: 'high',
  },
];

export const BASE_DETAILS_WORKFLOW_KEY = 'baseSimulationDetails';
export const SimulationBaseDetailsStep = () => {
  const [form] = Form.useForm();
  const setNavigationStep = useStoreActions((actions) => actions.simulation.setActiveNavigationStep);
  const megaProcessVersion =
    useStoreState((state) => state.partner.activePartner?.featureConfigurationInfo)?.megaProcessVersion || '2';
  const navigationStep = useStoreState((state) => state.simulation.activeNavigationStep);
  const { mutateAsync: createSimulation } = useCreateSimulation();
  const { mutateAsync: updateSimulation } = useUpdateSimulation();
  const createCostAdjustConfig = useStoreActions((actions) => actions.limit.createCostAdjustConfig);
  const putCostAdjustConfig = useStoreActions((actions) => actions.limit.putCostAdjustConfig);
  const fetchCostAdjustConfigById = useStoreActions((actions) => actions.limit.fetchCostAdjustConfigById);
  const { simulationType } = useParams<{ simulationType: SimulationType }>();
  const partner = useStoreState((state) => state.partner.activePartner);
  const simulationSetupData = useStoreState((state) => state.simulation.simulationSetupData);
  const setSimulationSetupData = useStoreActions((actions) => actions.simulation.setSimulationSetupData);
  const [query, setQuery] = useSearchParams();
  const simulationId = query.get(SIMULATION_ID_SEARCH_PARAM);
  const { isShipiumView } = useSession();
  const costAdjustLimitsFeatureFlag = partner?.featureToggles[BaseFeatureToggles.DYNAMIC_LIMITS] === 'true';
  const partnerId = useStoreState((state) => state.partner.activePartner?.partnerId);
  // v2 and the version picker are advanced + Shipium-only. Guided types stay on v1, and since
  // advanced==Shipium is only incidental today, we gate on isShipiumView explicitly so v2/the picker
  // can't leak to partner users if advanced access opens up before the migration completes.
  const isAdvanced = simulationType === 'advanced';
  const v2Available = isAdvanced && isShipiumView;
  const versionDefault: SimulationServiceVersion = v2Available ? DEFAULT_SIMULATION_SERVICE_VERSION : 'v1';
  const [pickedVersion, setPickedVersion] = useState<SimulationServiceVersion>(
    simulationSetupData.serviceVersion ?? versionDefault,
  );
  // On create the picker drives it; once created the version is locked, so read the stored stamp.
  const stampedVersion = simulationId ? (simulationSetupData.serviceVersion ?? versionDefault) : pickedVersion;

  const { data: partnerCarrierSummaries } = usePartnerCarrierSummaries({ partnerId });

  const linkedCostAdjustConfigId =
    simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]?.simulationShipmentOptions?.costAdjustConfigId;
  const linkedCostAdjustConfig = useStoreState((state) =>
    linkedCostAdjustConfigId ? state.limit.costAdjustConfigs[linkedCostAdjustConfigId] : undefined,
  );

  useEffect(() => {
    if (simulationId && simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]) {
      const formData = flattenBaseDetailsToFormData(simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]);
      form.setFieldsValue(formData);
    }
  }, [simulationId, simulationSetupData, form]);

  // Trigger the fetch when the linked config id appears or changes.
  // The thunk writes the result into the store; the next effect picks it up.
  useEffect(() => {
    if (!linkedCostAdjustConfigId) return;
    fetchCostAdjustConfigById({ costAdjustConfigId: linkedCostAdjustConfigId }).catch(() => {
      // Already surfaced via dispatchMessage in the thunk.
    });
  }, [linkedCostAdjustConfigId, fetchCostAdjustConfigById]);

  // Hydrate cost-adjust form fields when the linked config lands in the store.
  useEffect(() => {
    if (!linkedCostAdjustConfig) return;
    form.setFieldsValue({
      costAdjustLimitEnabled: true,
      costAdjustLimitTargetCarrier: linkedCostAdjustConfig.targetCarrier,
      costAdjustLimitExcludedCarriers: linkedCostAdjustConfig.excludedCarriers ?? [],
      costAdjustTargetBehavior: linkedCostAdjustConfig.costAdjustTargetBehavior ?? DEFAULT_LIMIT_BEHAVIOR,
      targetPercent:
        linkedCostAdjustConfig.targetPercent != null
          ? doubleToPercent(linkedCostAdjustConfig.targetPercent)
          : undefined,
      seedAdjustment: linkedCostAdjustConfig.seedAdjustment,
    });
  }, [linkedCostAdjustConfig, form]);

  const buildBasePayload = (formValues: any) => {
    const {
      name,
      description,
      executionPriority,
      includesHistoricalSelection = false,
      totalUpgradeCostTolerance,
      upgradeOverCheapestCostTolerance,
      carrierServiceMethodList,
      carrierListMode,
      ...restFields
    } = formValues;

    const shipmentOptions = shapeShipOptions({
      ...restFields,
      ...formatCarrierListsForStorage(carrierServiceMethodList, carrierListMode, partnerCarrierSummaries),
    });

    const basePayload = {
      simulationId: simulationId ?? '', // gotta keep ts happy <3
      name,
      description,
      executionPriority,
      simulationType,
      includesHistoricalSelection,
      simulationStatus: SimulationStatus.CONFIGURING,
      simulationShipmentOptions: {
        ...shipmentOptions,
        costAdjustConfigId: restFields.costAdjustLimitEnabled
          ? simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]?.simulationShipmentOptions?.costAdjustConfigId
          : undefined,
      },
      simulationCostingOptions: {
        totalUpgradeCostTolerance: totalUpgradeCostTolerance,
        upgradeOverCheapestCostTolerance: upgradeOverCheapestCostTolerance,
      },
      visibleToPartner: !isShipiumView,
    } as any;

    return { basePayload, restFields, name, description };
  };

  // Phase 2: create or update the linked CostAdjustConfig. Returns the configId.
  const persistCostAdjustConfig = async (
    restFields: any,
    name: string,
    description: string | undefined,
    persistedSimulationId: string,
  ): Promise<string> => {
    const existingCostAdjustConfigId =
      simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]?.simulationShipmentOptions?.costAdjustConfigId;
    const seedAdjustmentRaw = restFields.seedAdjustment;
    const configCommon = {
      name: `${name} (simulation)`.slice(0, 255),
      description: `Cost adjust config for simulation: ${description ?? ''}`.slice(0, 1024),
      targetPercent: percentToClampedFixedDouble(Number(restFields.targetPercent ?? 0)),
      costAdjustTargetBehavior: restFields.costAdjustTargetBehavior ?? DEFAULT_LIMIT_BEHAVIOR,
      evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
      excludedCarriers: restFields.costAdjustLimitExcludedCarriers ?? [],
      seedAdjustment:
        seedAdjustmentRaw !== undefined && seedAdjustmentRaw !== null && seedAdjustmentRaw !== ''
          ? Number(seedAdjustmentRaw)
          : undefined,
    };

    if (existingCostAdjustConfigId) {
      // Spread the freshly-fetched config underneath the UI edits so any
      // backend-managed fields the UI doesn't render (DCAS dial knobs:
      // boostPercent, lookbackHours, maxAdjustmentMagnitude, minEligibleCount,
      // recomputeCadence) survive the PUT replace. UI-managed fields override.
      // If the hydration effect's fetch hasn't resolved yet, refetch inline
      // rather than send a naked PUT that would null every backend-managed field.
      const existingConfig =
        linkedCostAdjustConfig ?? (await fetchCostAdjustConfigById({ costAdjustConfigId: existingCostAdjustConfigId }));
      if (!existingConfig) {
        throw new Error('Cost adjust config not loaded; refusing to PUT without existing fields.');
      }
      const putPayload: PartnerCostAdjustConfigPut = {
        ...existingConfig,
        ...configCommon,
      };
      await putCostAdjustConfig({
        costAdjustConfigId: existingCostAdjustConfigId,
        costAdjustConfig: putPayload,
      });
      return existingCostAdjustConfigId;
    }

    const createPayload: PartnerCostAdjustConfigCreate = {
      ...configCommon,
      targetCarrier: restFields.costAdjustLimitTargetCarrier,
      simulationId: persistedSimulationId,
    };
    const created = await createCostAdjustConfig(createPayload);
    return created.costAdjustConfigId;
  };

  const onSave = async () => {
    try {
      await form.validateFields();
    } catch (e) {
      console.error('Error validating simulation form', e);
      return;
    }

    const { basePayload, restFields, name, description } = buildBasePayload(form.getFieldsValue());
    const mutationAction = simulationId ? updateSimulation : createSimulation;
    try {
      const saved = await mutationAction({
        ...basePayload,
        simulationSetupData: {
          ...simulationSetupData,
          serviceVersion: stampedVersion,
          [BASE_DETAILS_WORKFLOW_KEY]: basePayload,
        },
      });
      const persistedSimulationId = saved.simulationId;

      // Anchor the URL's simulationId immediately after Phase 1 so any
      // failure downstream causes the next retry to update this simulation
      // rather than minting a new one.
      setQuery({ [SIMULATION_ID_SEARCH_PARAM]: persistedSimulationId });

      let finalPayload = basePayload;
      if (restFields.costAdjustLimitEnabled) {
        const costAdjustConfigId = await persistCostAdjustConfig(restFields, name, description, persistedSimulationId);
        finalPayload = {
          ...basePayload,
          simulationId: persistedSimulationId,
          simulationShipmentOptions: {
            ...basePayload.simulationShipmentOptions,
            costAdjustConfigId,
          },
        };
        await updateSimulation({
          ...finalPayload,
          simulationSetupData: {
            ...simulationSetupData,
            serviceVersion: stampedVersion,
            [BASE_DETAILS_WORKFLOW_KEY]: finalPayload,
          },
        });
      }

      setSimulationSetupData({
        ...simulationSetupData,
        serviceVersion: stampedVersion,
        [BASE_DETAILS_WORKFLOW_KEY]: finalPayload,
      });
      setNavigationStep(navigationStep + 1);
    } catch (e) {
      console.error('Error saving simulation', e);
      // Error toast is handled by the mutation hook's onError — don't double-dispatch here.
    }
  };

  return (
    <SimulationWorkflowCard simulationId={simulationId} onSave={onSave}>
      {(!simulationId || simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]) && (
        <Form layout="vertical" form={form}>
          {v2Available && (
            <Form.Item label={'Simulation Engine Version'}>
              {/* After create the version is locked: show the STORED stamp, disabled. */}
              <VersionPicker value={stampedVersion} onChange={setPickedVersion} disabled={!!simulationId} />
            </Form.Item>
          )}
          <Form.Item
            required={true}
            label={'Name'}
            extra={'Add a name for this simulation'}
            name={'name'}
            rules={[{ required: true, message: 'Please provide a name for your simulation' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            required={true}
            label={'Description'}
            extra={'Add a description for your simulation'}
            name={'description'}
            rules={[{ required: true, message: 'Please provide a description for your simulation' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            initialValue={'standard'}
            label={'Priority'}
            name={'executionPriority'}
            extra={
              'If you want simulations to run in an order other than when then were created specify the priority accordingly'
            }
          >
            <Select defaultValue={'standard'}>
              {SimulationPrioties.map((prio) => (
                <Select.Option value={prio.value}>{prio.title}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          {megaProcessVersion === '1' && isShipiumView && (
            <Form.Item
              initialValue={'2'}
              label={'Shiptime Configuration Version'}
              name={'shiptimeVersion'}
              extra={
                'V1 Configuration detected. You may choose to execute this simulation against V1 configuration with limited mapping options.'
              }
            >
              <Select defaultValue={'2'}>
                <Select.Option value={'1'}>Version 1</Select.Option>
                <Select.Option value={'2'}>Version 2</Select.Option>
              </Select>
            </Form.Item>
          )}
          <Collapse>
            <Collapse.Panel key={'cost'} header={'Costing Options (optional)'}>
              <Alert message="Optionally override production upgrade cost limits" />
              <CostingOptionsForm
                editMode={!!simulationSetupData[BASE_DETAILS_WORKFLOW_KEY]?.simulationCostingOptions && !!simulationId}
              />
            </Collapse.Panel>
            <Collapse.Panel key={'shipmentOptions'} header={'Shipment Options (optional)'}>
              <Alert message="Optionally override details of the shipments you are simulating" />
              <ShipmentOptionsForm />
            </Collapse.Panel>
            {costAdjustLimitsFeatureFlag && (
              <Collapse.Panel key={'shipmentLimits'} header={'Shipment Limits (optional)'}>
                <Alert message="Optionally override production shipment count limits" />
                <CostAdjustLimitsForm form={form} targetCarrierLocked={!!linkedCostAdjustConfigId} />
              </Collapse.Panel>
            )}
          </Collapse>
        </Form>
      )}
    </SimulationWorkflowCard>
  );
};
